"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";

export function RequestAccessForm() {
  const [status, setStatus] = useState<"idle" | "submitting" | "ok" | "err">(
    "idle",
  );
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setStatus("submitting");
    setErrorMsg(null);

    const form = e.currentTarget;
    const data = new FormData(form);

    try {
      const resp = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: data.get("email"),
          displayName: data.get("displayName"),
          websiteUrl: data.get("websiteUrl"),
          notes: data.get("notes"),
          source: "website",
        }),
      });

      if (!resp.ok) {
        const j = (await resp.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? `Request failed (${resp.status})`);
      }
      setStatus("ok");
      form.reset();
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : String(err));
      setStatus("err");
    }
  }

  if (status === "ok") {
    return (
      <div className="rounded-xl border border-accent/40 bg-accent/5 p-6">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-accent mb-2">
          Got it
        </p>
        <h2 className="text-2xl font-semibold leading-tight">
          Thanks &mdash; we&rsquo;ll be in touch.
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Adam reads every signup personally. Expect a reply within 24 hours
          (often much faster).
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label
          htmlFor="email"
          className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground block mb-1.5"
        >
          Email <span className="text-destructive">*</span>
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@studio.com"
          className="w-full bg-background/60 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>

      <div>
        <label
          htmlFor="displayName"
          className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground block mb-1.5"
        >
          Your name or studio
        </label>
        <input
          id="displayName"
          name="displayName"
          autoComplete="name"
          placeholder="Maria Costa Photography"
          className="w-full bg-background/60 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>

      <div>
        <label
          htmlFor="websiteUrl"
          className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground block mb-1.5"
        >
          Portfolio / website
        </label>
        <input
          id="websiteUrl"
          name="websiteUrl"
          type="url"
          autoComplete="url"
          placeholder="https://"
          className="w-full bg-background/60 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent"
        />
      </div>

      <div>
        <label
          htmlFor="notes"
          className="font-mono text-[10px] tracking-[0.22em] uppercase text-muted-foreground block mb-1.5"
        >
          Anything we should know?
        </label>
        <textarea
          id="notes"
          name="notes"
          rows={4}
          placeholder="Niche, library size, any specific infringers you're chasing..."
          className="w-full bg-background/60 border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-accent resize-none"
        />
      </div>

      {status === "err" && errorMsg ? (
        <p className="text-xs text-destructive">{errorMsg}</p>
      ) : null}

      <Button
        type="submit"
        disabled={status === "submitting"}
        size="lg"
      >
        {status === "submitting" ? "Sending\u2026" : "Request access"}
      </Button>
    </form>
  );
}
